import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import type { CartItem, Product } from 'utils/types';
import { productService, type VariantPrice } from 'services/product.service';
import { useCurrency } from 'contexts/CurrencyContext';
import { useAuth } from 'contexts/AuthContext';
import { calculateGoodsNet } from 'utils/orderTotals';
import { applyQuantityBreakForQuantity } from 'utils/quantityBreaks';
import {
  cartItemToGaItem,
  productToGaItem,
  trackAddToCart,
  trackRemoveFromCart,
} from 'utils/analytics';

interface CartState {
  items: CartItem[];
  itemCount: number;
  isLoading: boolean;
  selectedItems: string[];
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number; variants?: { mould?: string; shade?: string } } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'REMOVE_SELECTED'; payload: string[] }
  | { type: 'SELECT_ALL' }
  | { type: 'DESELECT_ALL' }
  | { type: 'TOGGLE_SELECTION'; payload: string }
    | {
        type: 'UPDATE_PRICES';
        payload: Array<{
          itemId: string;
          price: number;
          priceIncVat?: number;
          currency: string;
          taxRate?: number;
          taxRateId?: number;
          originalPrice?: number;
          originalPriceIncVat?: number;
          discountPercent?: number;
          basePrice?: number;
          basePriceIncVat?: number;
          quantityBreaks?: CartItem['quantityBreaks'];
        }>;
      };

type PriceUpdatePayload = Extract<CartAction, { type: 'UPDATE_PRICES' }>['payload'][number];

function buildQuantitiesFromItems(items: CartItem[]): Record<number, number> {
  const quantities: Record<number, number> = {};
  items.forEach((item) => {
    const variantId = parseInt(item.productId, 10);
    if (!isNaN(variantId) && variantId > 0) {
      quantities[variantId] = item.quantity;
    }
  });
  return quantities;
}

function resolveCartItemUnitPrices(
  item: Pick<CartItem, 'quantityBreaks' | 'basePrice' | 'basePriceIncVat' | 'price' | 'priceIncVat' | 'originalPrice' | 'originalPriceIncVat' | 'discountPercent'>,
  quantity: number,
): Pick<CartItem, 'price' | 'priceIncVat' | 'originalPrice' | 'originalPriceIncVat' | 'discountPercent'> {
  const baseExVat = item.basePrice ?? item.price;
  const baseIncVat = item.basePriceIncVat ?? item.priceIncVat ?? baseExVat;

  if (!item.quantityBreaks?.length || baseExVat == null) {
    return {
      price: item.price,
      priceIncVat: item.priceIncVat,
      originalPrice: item.originalPrice,
      originalPriceIncVat: item.originalPriceIncVat,
      discountPercent: item.discountPercent,
    };
  }

  const resolved = applyQuantityBreakForQuantity(
    baseExVat,
    baseIncVat,
    item.quantityBreaks,
    quantity,
  );

  if (!resolved.applied) {
    return {
      price: baseExVat,
      priceIncVat: baseIncVat,
      originalPrice: item.originalPrice,
      originalPriceIncVat: item.originalPriceIncVat,
      discountPercent: item.discountPercent,
    };
  }

  return {
    price: resolved.exVat,
    priceIncVat: resolved.incVat,
    originalPrice: baseExVat,
    originalPriceIncVat: baseIncVat,
    discountPercent: resolved.percentDiscount ?? item.discountPercent,
  };
}

async function fetchCartPriceUpdates(
  items: CartItem[],
  currencyId: number | undefined,
  currencyCode: string,
  signal?: AbortSignal,
): Promise<PriceUpdatePayload[]> {
  if (items.length === 0) {
    return [];
  }

  const itemIdToVariantIdMap = new Map<string, number>();
  const variantIdSet = new Set<number>();

  items.forEach((item) => {
    const variantId = parseInt(item.productId, 10);
    if (!isNaN(variantId) && variantId > 0) {
      itemIdToVariantIdMap.set(item.id, variantId);
      variantIdSet.add(variantId);
    }
  });

  const variantIds = Array.from(variantIdSet);
  if (variantIds.length === 0) {
    return [];
  }

  const quantities = buildQuantitiesFromItems(items);
  const { prices: priceMap, quantityBreaks: quantityBreaksMap } = await productService.getBatchPrices(
    variantIds,
    currencyId,
    signal,
    quantities,
  );

  const priceUpdates: PriceUpdatePayload[] = [];
  items.forEach((item) => {
    const variantId = itemIdToVariantIdMap.get(item.id);
    if (variantId) {
      priceUpdates.push(
        buildPriceUpdate(
          item.id,
          currencyCode,
          priceMap.get(variantId),
          item,
          quantityBreaksMap.get(variantId),
        ),
      );
    }
  });

  return priceUpdates;
}

function buildPriceUpdate(
  itemId: string,
  currency: string,
  priceData: VariantPrice | undefined,
  fallback: CartItem,
  quantityBreaks?: CartItem['quantityBreaks'],
): PriceUpdatePayload {
  const baseExVat =
    priceData?.unitPriceBeforeQuantityBreakExVat ??
    priceData?.originalRspExVat ??
    fallback.basePrice ??
    priceData?.rspExVat ??
    fallback.price;
  const baseIncVat =
    priceData?.unitPriceBeforeQuantityBreakIncVat ??
    priceData?.originalRspIncVat ??
    fallback.basePriceIncVat ??
    priceData?.rspIncVat ??
    fallback.priceIncVat;

  return {
    itemId,
    price: priceData?.rspExVat ?? fallback.price,
    priceIncVat: priceData?.rspIncVat ?? fallback.priceIncVat,
    originalPrice: priceData?.originalRspExVat ?? fallback.originalPrice,
    originalPriceIncVat: priceData?.originalRspIncVat ?? fallback.originalPriceIncVat,
    discountPercent: priceData?.discountPercent ?? priceData?.quantityBreakPercent ?? fallback.discountPercent,
    currency,
    taxRate: priceData?.taxRate ?? fallback.taxRate,
    taxRateId: priceData?.taxRateId ?? fallback.taxRateId,
    basePrice: baseExVat,
    basePriceIncVat: baseIncVat,
    quantityBreaks: quantityBreaks ?? fallback.quantityBreaks,
  };
}

const initialState: CartState = {
  items: [],
  itemCount: 0,
  isLoading: false,
  selectedItems: [],
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity, variants } = action.payload;

      // Generate unique ID for this cart item (includes variant info)
      const variantId = `${product.id}-${variants?.mould || 'no-mould'}-${variants?.shade || 'no-shade'}`;

      const existingItem = state.items.find(item => item.id === variantId);

      const productAny = product as {
        measurements?: { weight?: number };
        weight?: number;
        priceData?: VariantPrice;
        quantityBreaks?: CartItem['quantityBreaks'];
      };
      const priceData = productAny.priceData;
      const quantityBreaks = productAny.quantityBreaks;
      const basePrice =
        priceData?.unitPriceBeforeQuantityBreakExVat ??
        priceData?.originalRspExVat ??
        product.price;
      const basePriceIncVat =
        priceData?.unitPriceBeforeQuantityBreakIncVat ??
        priceData?.originalRspIncVat ??
        priceData?.rspIncVat;

      let newItems: CartItem[];
      if (existingItem) {
        newItems = state.items.map((item) => {
          if (item.id !== variantId) {
            return item;
          }
          const newQuantity = item.quantity + quantity;
          const priced = resolveCartItemUnitPrices(item, newQuantity);
          return {
            ...item,
            quantity: newQuantity,
            ...priced,
          };
        });
      } else {
        const weight = productAny.measurements?.weight ?? productAny.weight ?? 0;
        const draftItem: CartItem = {
          id: variantId,
          productId: product.id,
          quantity,
          selectedMould: variants?.mould,
          selectedShade: variants?.shade,
          productName: product.name,
          productCode: product.code,
          description: product.description,
          price: product.price,
          priceIncVat: priceData?.rspIncVat,
          originalPrice: priceData?.originalRspExVat,
          originalPriceIncVat: priceData?.originalRspIncVat,
          discountPercent: priceData?.discountPercent,
          currency: product.currency,
          taxRate: priceData?.taxRate,
          taxRateId: priceData?.taxRateId,
          images: product.images || [],
          category: product.category,
          weight,
          quantityBreaks,
          basePrice,
          basePriceIncVat,
        };
        const priced = resolveCartItemUnitPrices(draftItem, quantity);

        newItems = [...state.items, {
          ...draftItem,
          ...priced,
        }];
      }

      // Calculate total and item count
      const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount: newItemCount,
      };
    }

    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount: newItemCount,
      };
    }

    case 'UPDATE_QUANTITY': {
      const { productId, quantity } = action.payload;
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: productId });
      }

      const newItems = state.items.map((item) => {
        if (item.id !== productId) {
          return item;
        }
        const priced = resolveCartItemUnitPrices(item, quantity);
        return {
          ...item,
          quantity,
          ...priced,
        };
      });
      const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount: newItemCount,
      };
    }

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        itemCount: 0,
      };

    case 'LOAD_CART': {
      const items = action.payload;
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items,
        itemCount,
      };
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'REMOVE_SELECTED': {
      const newItems = state.items.filter(item => !action.payload.includes(item.id));
      const newItemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount: newItemCount,
        selectedItems: [],
      };
    }

    case 'SELECT_ALL':
      return {
        ...state,
        selectedItems: state.items.map(item => item.id),
      };

    case 'DESELECT_ALL':
      return {
        ...state,
        selectedItems: [],
      };

    case 'TOGGLE_SELECTION': {
      const itemId = action.payload;
      const isSelected = state.selectedItems.includes(itemId);

      return {
        ...state,
        selectedItems: isSelected
          ? state.selectedItems.filter(id => id !== itemId)
          : [...state.selectedItems, itemId],
      };
    }

    case 'UPDATE_PRICES': {
      const priceUpdates = action.payload;
      const priceMap = new Map(priceUpdates.map(update => [update.itemId, update]));
      
      const newItems = state.items.map(item => {
        const update = priceMap.get(item.id);
        if (update) {
          const nextItem = {
            ...item,
            price: update.price,
            priceIncVat: update.priceIncVat,
            originalPrice: update.originalPrice,
            originalPriceIncVat: update.originalPriceIncVat,
            discountPercent: update.discountPercent,
            currency: update.currency,
            taxRate: update.taxRate,
            taxRateId: update.taxRateId,
            basePrice: update.basePrice ?? item.basePrice,
            basePriceIncVat: update.basePriceIncVat ?? item.basePriceIncVat,
            quantityBreaks: update.quantityBreaks ?? item.quantityBreaks,
          };
          return {
            ...nextItem,
            ...resolveCartItemUnitPrices(nextItem, nextItem.quantity),
          };
        }
        return item;
      });

      return {
        ...state,
        items: newItems,
      };
    }

    default:
      return state;
  }
};

interface CartContextType extends CartState {
  addItem: (product: Product, quantity?: number, variants?: { mould?: string; shade?: string }) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
  getItemVariants: (productId: string) => { mould?: string; shade?: string } | null;
  removeSelected: (itemIds: string[]) => void;
  selectAll: () => void;
  deselectAll: () => void;
  toggleSelection: (itemId: string) => void;
  isSelected: (itemId: string) => boolean;
  getTotal: (products?: Product[]) => number;
  refreshPrices: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { selectedCurrency, currencyId } = useCurrency();
  const { isAuthenticated } = useAuth();
  const previousCurrencyRef = useRef<string>(selectedCurrency.code);
  const itemsSnapshotRef = useRef<CartItem[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousAuthRef = useRef<boolean | null>(null);
  const cartHydratedRef = useRef(false);
  const initialPriceRefreshDoneRef = useRef(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const loadCartFromStorage = () => {
      try {
        const savedCart = localStorage.getItem('cart_data');
        if (savedCart) {
          const cartItems: any[] = JSON.parse(savedCart);

          // Migrate old cart items to new format
          const migratedItems: CartItem[] = cartItems
            .filter(item => {
              // Filter out items that don't have required product data
              // These are old items that can't be displayed properly
              return item.productName && item.productCode && item.price !== undefined;
            })
            .map(item => {
              // Ensure id field exists
              const migrated: CartItem = { ...item };
              if (!migrated.id) {
                migrated.id = `${item.productId}-${item.selectedMould || 'no-mould'}-${item.selectedShade || 'no-shade'}`;
              }
              // Preserve weight if present, otherwise default to 0
              if (migrated.weight === undefined) {
                migrated.weight = 0;
              }
              return migrated;
            });

          if (migratedItems.length > 0) {
            dispatch({ type: 'LOAD_CART', payload: migratedItems });
          } else if (cartItems.length > 0) {
            // If we have old items but they can't be migrated, clear the cart
            console.warn('Cart items found but missing required product data. Clearing cart.');
            localStorage.removeItem('cart_data');
          }
        }
      } catch (error) {
        console.error('Error loading cart from storage:', error);
      }
    };

    loadCartFromStorage();
    cartHydratedRef.current = true;
  }, []);

  // Sync prices and quantity breaks once after cart is restored from localStorage
  useEffect(() => {
    if (!cartHydratedRef.current || initialPriceRefreshDoneRef.current) {
      return;
    }

    if (state.items.length === 0) {
      initialPriceRefreshDoneRef.current = true;
      return;
    }

    initialPriceRefreshDoneRef.current = true;
    let cancelled = false;

    const refreshOnMount = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const priceUpdates = await fetchCartPriceUpdates(
          state.items,
          currencyId,
          selectedCurrency.code,
        );
        if (!cancelled && priceUpdates.length > 0) {
          dispatch({ type: 'UPDATE_PRICES', payload: priceUpdates });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error refreshing cart prices on mount:', error);
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    };

    refreshOnMount();

    return () => {
      cancelled = true;
    };
  }, [state.items, currencyId, selectedCurrency.code]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart_data', JSON.stringify(state.items));
  }, [state.items]);

  // Store items snapshot when currency changes
  useEffect(() => {
    itemsSnapshotRef.current = state.items;
  }, [state.items]);

  // Update prices when currency changes
  useEffect(() => {
    const updatePricesForCurrencyChange = async () => {
      // Skip if currency hasn't actually changed (initial load)
      if (previousCurrencyRef.current === selectedCurrency.code) {
        return;
      }

      // Update the ref immediately to prevent duplicate calls
      const currentCurrency = selectedCurrency.code;
      previousCurrencyRef.current = currentCurrency;

      // Get items snapshot from ref (captured at currency change time)
      const itemsSnapshot = itemsSnapshotRef.current;

      // Skip if cart is empty
      if (itemsSnapshot.length === 0) {
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // OPTIMIZED: Cancel any in-flight requests to prevent race conditions
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const priceUpdates = await fetchCartPriceUpdates(
          itemsSnapshot,
          currencyId,
          currentCurrency,
          abortController.signal,
        );

        if (priceUpdates.length > 0) {
          dispatch({ type: 'UPDATE_PRICES', payload: priceUpdates });
        }
      } catch (error) {
        // OPTIMIZED: Don't log abort errors (expected when currency changes quickly)
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error updating prices for currency change:', error);
        }
        // On error, keep existing prices (don't update)
      } finally {
        // OPTIMIZED: Clear abort controller reference
        abortControllerRef.current = null;
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    updatePricesForCurrencyChange();
  }, [selectedCurrency.code, currencyId]);

  // Refresh prices when user logs in mid-session (initial load handled by mount refresh)
  useEffect(() => {
    const refreshPricesOnLogin = async () => {
      if (previousAuthRef.current === null) {
        previousAuthRef.current = isAuthenticated;
        return;
      }

      if (isAuthenticated && !previousAuthRef.current) {
        previousAuthRef.current = true;

        const itemsAtLogin = state.items;

        if (itemsAtLogin.length > 0) {
          try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const priceUpdates = await fetchCartPriceUpdates(
              itemsAtLogin,
              currencyId,
              selectedCurrency.code,
            );
            if (priceUpdates.length > 0) {
              dispatch({ type: 'UPDATE_PRICES', payload: priceUpdates });
            }
          } catch (error) {
            console.error('Error refreshing cart prices after login:', error);
          } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        }
      } else if (!isAuthenticated) {
        previousAuthRef.current = false;
      }
    };

    refreshPricesOnLogin();
  }, [isAuthenticated, currencyId, selectedCurrency.code, state.items]);

  // OPTIMIZED: Memoize dispatch functions to prevent unnecessary re-renders
  const addItem = useCallback((product: Product, quantity: number = 1, variants?: { mould?: string; shade?: string }) => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity, variants } });
    trackAddToCart(
      productToGaItem(product, quantity, variants),
      selectedCurrency.code,
    );
  }, [selectedCurrency.code]);

  const removeItem = useCallback((itemId: string) => {
    const item = state.items.find((i) => i.id === itemId);
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
    if (item) {
      trackRemoveFromCart(cartItemToGaItem(item), selectedCurrency.code);
    }
  }, [state.items, selectedCurrency.code]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item) {
      const delta = quantity - item.quantity;
      if (delta > 0) {
        trackAddToCart(cartItemToGaItem(item, delta), selectedCurrency.code);
      } else if (delta < 0) {
        trackRemoveFromCart(cartItemToGaItem(item, Math.abs(delta)), selectedCurrency.code);
      }
    }
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId: itemId, quantity } });
  }, [state.items, selectedCurrency.code]);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const getItemQuantity = useCallback((productId: string): number => {
    const item = state.items.find(item => item.productId === productId);
    return item ? item.quantity : 0;
  }, [state.items]);

  const getItemVariants = useCallback((productId: string): { mould?: string; shade?: string } | null => {
    const item = state.items.find(item => item.productId === productId);
    if (!item) return null;
    return {
      mould: item.selectedMould,
      shade: item.selectedShade
    };
  }, [state.items]);

  // OPTIMIZED: Memoize dispatch functions that don't depend on state
  const removeSelected = useCallback((itemIds: string[]) => {
    state.items
      .filter((item) => itemIds.includes(item.id))
      .forEach((item) => {
        trackRemoveFromCart(cartItemToGaItem(item), selectedCurrency.code);
      });
    dispatch({ type: 'REMOVE_SELECTED', payload: itemIds });
  }, [state.items, selectedCurrency.code]);

  const selectAll = useCallback(() => {
    dispatch({ type: 'SELECT_ALL' });
  }, []);

  const deselectAll = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL' });
  }, []);

  const toggleSelection = useCallback((itemId: string) => {
    dispatch({ type: 'TOGGLE_SELECTION', payload: itemId });
  }, []);

  // OPTIMIZED: Memoize isSelected to prevent unnecessary re-renders
  const isSelected = useCallback((itemId: string): boolean => {
    return state.selectedItems.includes(itemId);
  }, [state.selectedItems]);

  // OPTIMIZED: Memoize getTotal to prevent recalculation on every render
  const getTotal = useCallback((products?: Product[]): number => {
    // Use stored product prices from cart items (preferred)
    // Fallback to products array if provided (for backward compatibility)
    const itemsForCalculation = state.items.map(item => {
      // If item has price, use it directly
      if (item.price !== undefined && item.price !== null) {
        return { price: item.price, quantity: item.quantity };
      }
      
      // Fallback: lookup in products array
      if (products) {
        const product = products.find(p => p.id === item.productId);
        if (product) {
          return { price: product.price, quantity: item.quantity };
        }
      }
      
      // Last resort: return zero price item (will be handled by calculateGoodsNet)
      return { price: 0, quantity: item.quantity };
    });
    
    return calculateGoodsNet(itemsForCalculation);
  }, [state.items]);

  // Refresh cart prices (e.g., after login to get customer-specific pricing)
  const refreshPrices = useCallback(async () => {
    if (state.items.length === 0) {
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const priceUpdates = await fetchCartPriceUpdates(
        state.items,
        currencyId,
        selectedCurrency.code,
      );

      if (priceUpdates.length > 0) {
        dispatch({ type: 'UPDATE_PRICES', payload: priceUpdates });
      }
    } catch (error) {
      console.error('Error refreshing cart prices:', error);
      // On error, keep existing prices
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.items, currencyId, selectedCurrency.code]);

  // OPTIMIZED: Memoize context value to prevent unnecessary re-renders of all consumers
  // Only recreate when state or functions actually change
  const value: CartContextType = useMemo(() => ({
    ...state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getItemVariants,
    removeSelected,
    selectAll,
    deselectAll,
    toggleSelection,
    isSelected,
    getTotal,
    refreshPrices,
  }), [
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getItemVariants,
    removeSelected,
    selectAll,
    deselectAll,
    toggleSelection,
    isSelected,
    getTotal,
    refreshPrices,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}; 